import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, FileText, Building2, Database, ListChecks,
  ScrollText, Save, Download, RefreshCw, AlertTriangle,
  Plus, Trash2, X as XIcon,
} from "lucide-react";

const STATUS_OPTIONS = [
  { v: "todo", label: "Da fare", c: "bg-slate-100 text-slate-600" },
  { v: "in_review", label: "In revisione", c: "bg-amber-100 text-amber-700" },
  { v: "done", label: "Completato", c: "bg-emerald-100 text-emerald-700" },
  { v: "na", label: "Non applicabile", c: "bg-slate-200 text-slate-500" },
];
const SUB_TABS = [
  { k: "stato", label: "Stato GDPR", I: ListChecks },
  { k: "documenti", label: "Documenti", I: FileText },
  { k: "fornitori", label: "Fornitori / DPA", I: Building2 },
  { k: "registro", label: "Registro trattamenti", I: ScrollText },
  { k: "breaches", label: "Data breach", I: AlertTriangle },
  { k: "consents", label: "Consensi (overview)", I: ShieldCheck },
  { k: "backup", label: "Backup & restore", I: Database },
  { k: "audit", label: "Audit log", I: ScrollText },
];

function Disclaimer({ children }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2 mb-4">
      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
      <div>{children || "Bozza operativa generata da Prime Vision Software. Da validare con consulente privacy/DPO prima dell'uso ufficiale. Il software NON è dichiarato GDPR conforme al 100% né legalmente approvato."}</div>
    </div>
  );
}

function ChecklistTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/admin/compliance/checklist"); setItems(data); }
    catch (e) { toast.error(apiErr(e, "Errore caricamento checklist")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, payload) => {
    try {
      const { data } = await api.patch(`/admin/compliance/checklist/${id}`, payload);
      setItems((p) => p.map((x) => x.id === id ? data : x));
    } catch (e) { toast.error(apiErr(e, "Errore aggiornamento")); }
  };

  const totals = useMemo(() => {
    const t = { todo: 0, in_review: 0, done: 0, na: 0 };
    items.forEach((it) => { t[it.status] = (t[it.status] || 0) + 1; });
    return t;
  }, [items]);

  return (
    <div data-testid="compliance-checklist-tab">
      <Disclaimer />
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_OPTIONS.map((s) => (
          <span key={s.v} className={`${s.c} px-3 py-1 rounded-full text-xs font-bold`}>
            {s.label}: {totals[s.v] || 0}
          </span>
        ))}
      </div>
      {loading ? <div className="text-slate-500 py-6 text-center">Caricamento…</div> : (
        <ul className="space-y-2" data-testid="checklist-items">
          {items.map((it) => {
            const cur = STATUS_OPTIONS.find((s) => s.v === it.status) || STATUS_OPTIONS[0];
            return (
              <li key={it.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3" data-testid={`checklist-${it.key}`}>
                <div className="flex flex-wrap items-start gap-3 justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-brand-navy">{it.label}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                      key: <b>{it.key}</b> · agg.{it.updated_at ? new Date(it.updated_at).toLocaleString("it-IT") : "—"}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end min-w-[200px]">
                    <span className={`${cur.c} px-2 py-0.5 rounded-full text-[10px] font-bold`}>{cur.label}</span>
                    <select
                      value={it.status}
                      onChange={(e) => patch(it.id, { status: e.target.value })}
                      className="text-xs border border-[#E2E8F0] rounded px-2 py-1 bg-white"
                      data-testid={`checklist-status-${it.key}`}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
                <textarea
                  defaultValue={it.note || ""}
                  placeholder="Note interne…"
                  onBlur={(e) => { if ((e.target.value || "") !== (it.note || "")) patch(it.id, { note: e.target.value }); }}
                  data-testid={`checklist-note-${it.key}`}
                  className="mt-2 w-full text-xs border border-[#E2E8F0] rounded p-2 resize-none"
                  rows={2}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [active, setActive] = useState(null);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const loadList = async () => {
    try { const { data } = await api.get("/admin/compliance/documents"); setDocs(data); if (!activeKey && data.length) setActiveKey(data[0].key); }
    catch (e) { toast.error(apiErr(e, "Errore caricamento documenti")); }
  };
  useEffect(() => { loadList(); }, []);

  useEffect(() => {
    if (!activeKey) { setActive(null); setContent(""); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/compliance/documents/${activeKey}`);
        setActive(data); setContent(data.content_md || "");
      } catch (e) { toast.error(apiErr(e, "Errore documento")); }
    })();
  }, [activeKey]);

  const save = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/compliance/documents/${active.key}`, { content_md: content });
      setActive(data); toast.success("Documento salvato");
      loadList();
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio")); }
    finally { setBusy(false); }
  };

  const download = async (fmt) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/compliance/documents/${active.key}/download.${fmt}`;
      const token = localStorage.getItem("pv_token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${active.key}_v${(active.version || "1.0").replace(".", "_")}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(`Download ${fmt.toUpperCase()} fallito`); }
  };

  const patchStatus = async (status) => {
    try {
      const { data } = await api.patch(`/admin/compliance/documents/${active.key}`, { status });
      setActive(data); toast.success("Stato aggiornato"); loadList();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  return (
    <div data-testid="compliance-documents-tab">
      <Disclaimer />
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-2 self-start">
          <ul className="space-y-1" data-testid="doc-list">
            {docs.map((d) => (
              <li key={d.key}>
                <button
                  data-testid={`doc-item-${d.key}`}
                  onClick={() => setActiveKey(d.key)}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm ${activeKey === d.key ? "bg-brand-azure/15 text-brand-navy font-bold" : "hover:bg-slate-50 text-slate-700"}`}
                >
                  <div className="truncate">{d.title}</div>
                  <div className="text-[10px] text-slate-500">v{d.version} · {d.status}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
          {!active ? <div className="text-slate-500 py-8 text-center">Seleziona un documento</div> : (
            <>
              <div className="flex flex-wrap items-center gap-2 justify-between mb-3">
                <div className="min-w-0">
                  <div className="font-extrabold text-brand-navy">{active.title}</div>
                  <div className="text-xs text-slate-500">Versione {active.version} · agg.{active.updated_at ? new Date(active.updated_at).toLocaleString("it-IT") : "—"}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={active.status}
                    onChange={(e) => patchStatus(e.target.value)}
                    data-testid="doc-status-select"
                    className="text-xs border border-[#E2E8F0] rounded px-2 py-1 bg-white"
                  >
                    <option value="draft">bozza</option>
                    <option value="in_review">in revisione</option>
                    <option value="approved_internal">approvato internamente</option>
                  </select>
                  <button onClick={() => download("md")} data-testid="doc-dl-md" className="text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded flex items-center gap-1"><Download size={12} /> MD</button>
                  <button onClick={() => download("pdf")} data-testid="doc-dl-pdf" className="text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded flex items-center gap-1"><Download size={12} /> PDF</button>
                  <button onClick={save} disabled={busy} data-testid="doc-save" className="text-xs font-bold bg-brand-navy hover:bg-brand-navyDark disabled:opacity-50 text-white px-3 py-1.5 rounded flex items-center gap-1"><Save size={12} /> Salva</button>
                </div>
              </div>
              <textarea
                data-testid="doc-content-editor"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={22}
                className="w-full border border-[#E2E8F0] rounded p-3 text-xs font-mono leading-relaxed"
                spellCheck={false}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function VendorsTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "Responsabile esterno", data_categories: "", dpa_status: "to_acquire", note: "" });

  const load = async () => {
    try { const { data } = await api.get("/admin/compliance/vendors"); setList(data); }
    catch (e) { toast.error(apiErr(e, "Errore caricamento")); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Nome obbligatorio"); return; }
    try { await api.post("/admin/compliance/vendors", form); setOpen(false); setForm({ name: "", role: "Responsabile esterno", data_categories: "", dpa_status: "to_acquire", note: "" }); load(); toast.success("Fornitore creato"); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  const patch = async (v, payload) => {
    try { await api.patch(`/admin/compliance/vendors/${v.id}`, payload); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  const del = async (v) => {
    if (!confirm(`Eliminare il fornitore ${v.name}?`)) return;
    try { await api.delete(`/admin/compliance/vendors/${v.id}`); load(); toast.success("Eliminato"); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const STATUSES = [
    { v: "to_acquire", label: "DPA da acquisire", c: "bg-amber-100 text-amber-700" },
    { v: "acquired", label: "DPA acquisito", c: "bg-emerald-100 text-emerald-700" },
    { v: "na", label: "Non applicabile", c: "bg-slate-200 text-slate-500" },
  ];

  return (
    <div data-testid="compliance-vendors-tab">
      <Disclaimer />
      <div className="flex justify-end mb-3">
        <button onClick={() => setOpen(true)} data-testid="vendor-add-btn" className="bg-brand-navy hover:bg-brand-navyDark text-white px-3 py-1.5 rounded text-sm font-bold flex items-center gap-1"><Plus size={14} /> Aggiungi fornitore</button>
      </div>
      <ul className="space-y-2" data-testid="vendor-list">
        {list.map((v) => {
          const st = STATUSES.find((s) => s.v === v.dpa_status) || STATUSES[0];
          return (
            <li key={v.id} data-testid={`vendor-${v.id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-3">
              <div className="flex flex-wrap items-start gap-3 justify-between">
                <div className="min-w-0">
                  <div className="font-bold text-brand-navy">{v.name}</div>
                  <div className="text-xs text-slate-500">{v.role}</div>
                  <div className="text-xs text-slate-700 mt-1"><b>Dati:</b> {v.data_categories || "—"}</div>
                </div>
                <div className="flex flex-col items-end gap-2 min-w-[180px]">
                  <span className={`${st.c} px-2 py-0.5 rounded-full text-[10px] font-bold`}>{st.label}</span>
                  <select value={v.dpa_status} onChange={(e) => patch(v, { dpa_status: e.target.value })} className="text-xs border border-[#E2E8F0] rounded px-2 py-1 bg-white">
                    {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                  <button onClick={() => del(v)} className="text-[10px] text-red-600 hover:underline flex items-center gap-1"><Trash2 size={11} /> Elimina</button>
                </div>
              </div>
              <textarea defaultValue={v.note || ""} placeholder="Note interne…" onBlur={(e) => { if ((e.target.value || "") !== (v.note || "")) patch(v, { note: e.target.value }); }} className="mt-2 w-full text-xs border border-[#E2E8F0] rounded p-2 resize-none" rows={2} />
            </li>
          );
        })}
      </ul>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-5 max-w-md w-full">
            <div className="flex items-center justify-between mb-3">
              <div className="font-extrabold text-brand-navy">Nuovo fornitore</div>
              <button onClick={() => setOpen(false)}><XIcon size={18} /></button>
            </div>
            <div className="space-y-2 text-sm">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome fornitore" className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
              <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Ruolo privacy" className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
              <input value={form.data_categories} onChange={(e) => setForm({ ...form, data_categories: e.target.value })} placeholder="Dati trattati" className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
              <select value={form.dpa_status} onChange={(e) => setForm({ ...form, dpa_status: e.target.value })} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 bg-white">
                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
              <button onClick={create} data-testid="vendor-create-submit" className="bg-brand-navy text-white px-3 py-1.5 rounded font-bold w-full">Crea</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BackupTab() {
  const [info, setInfo] = useState(null);
  const [tests, setTests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ outcome: "ok", note: "", backup_ref: "" });

  const load = async () => {
    try { const { data } = await api.get("/admin/compliance/backup-info"); setInfo(data.info || {}); setTests(data.tests || []); }
    catch (e) { toast.error(apiErr(e, "Errore caricamento")); }
  };
  useEffect(() => { load(); }, []);

  const saveInfo = async (payload) => {
    try { const { data } = await api.patch("/admin/compliance/backup-info", payload); setInfo(data); toast.success("Salvato"); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const createTest = async () => {
    try { await api.post("/admin/compliance/restore-tests", form); setOpen(false); setForm({ outcome: "ok", note: "", backup_ref: "" }); load(); toast.success("Test registrato"); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  if (!info) return <div className="text-slate-500 py-8 text-center">Caricamento…</div>;

  return (
    <div data-testid="compliance-backup-tab">
      <Disclaimer>Il backup deve essere verificato periodicamente con prova di ripristino. Non considerare il backup &quot;a norma&quot; finché non è testato + DPA con il provider di storage.</Disclaimer>
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Provider</label>
          <input defaultValue={info.provider || ""} onBlur={(e) => { if (e.target.value !== info.provider) saveInfo({ provider: e.target.value }); }} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 mt-1" data-testid="backup-provider" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Frequenza</label>
          <input defaultValue={info.frequency || ""} onBlur={(e) => { if (e.target.value !== info.frequency) saveInfo({ frequency: e.target.value }); }} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 mt-1" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Retention (giorni)</label>
          <input type="number" defaultValue={info.retention_days || 30} onBlur={(e) => { const n = parseInt(e.target.value, 10); if (n && n !== info.retention_days) saveInfo({ retention_days: n }); }} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 mt-1" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Stato off-site</label>
          <select defaultValue={info.offsite_status || "to_verify"} onChange={(e) => saveInfo({ offsite_status: e.target.value })} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 mt-1 bg-white">
            <option value="configured">Configurato</option>
            <option value="not_configured">Non configurato</option>
            <option value="to_verify">Da verificare</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Ultimo backup noto</label>
          <input defaultValue={info.last_backup_at || ""} onBlur={(e) => { if (e.target.value !== info.last_backup_at) saveInfo({ last_backup_at: e.target.value }); }} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 mt-1" placeholder="ISO date o testo libero" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Note</label>
          <textarea defaultValue={info.notes || ""} onBlur={(e) => { if (e.target.value !== info.notes) saveInfo({ notes: e.target.value }); }} rows={2} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 mt-1" />
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="font-extrabold text-brand-navy">Registro test restore</div>
          <button onClick={() => setOpen(true)} data-testid="restore-add-btn" className="text-xs bg-brand-navy hover:bg-brand-navyDark text-white px-3 py-1.5 rounded font-bold flex items-center gap-1"><Plus size={12} /> Registra test restore</button>
        </div>
        {tests.length === 0 ? <div className="text-slate-500 italic text-sm py-3">Nessun test registrato.</div> : (
          <ul className="space-y-2" data-testid="restore-tests-list">
            {tests.map((t) => (
              <li key={t.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 text-sm flex flex-wrap gap-3 justify-between">
                <div>
                  <div className="font-bold text-brand-navy">{new Date(t.tested_at).toLocaleString("it-IT")} · {t.operator}</div>
                  <div className="text-xs text-slate-700">{t.note || "—"}</div>
                  {t.backup_ref && <div className="text-[10px] text-slate-500">Ref: {t.backup_ref}</div>}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold h-fit ${t.outcome === "ok" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{t.outcome.toUpperCase()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-5 max-w-md w-full">
            <div className="flex items-center justify-between mb-3">
              <div className="font-extrabold text-brand-navy">Registra test restore</div>
              <button onClick={() => setOpen(false)}><XIcon size={18} /></button>
            </div>
            <div className="space-y-2 text-sm">
              <select value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 bg-white">
                <option value="ok">Esito OK</option>
                <option value="failed">Esito FAILED</option>
              </select>
              <input value={form.backup_ref} onChange={(e) => setForm({ ...form, backup_ref: e.target.value })} placeholder="Backup ref (data/snapshot)" className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note operative…" rows={3} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
              <button onClick={createTest} data-testid="restore-create-submit" className="bg-brand-navy text-white px-3 py-1.5 rounded font-bold w-full">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ action: "", actor: "", date_from: "", date_to: "" });
  const load = async () => {
    try {
      const { data } = await api.get("/admin/compliance/audit-log", { params: { ...filters, limit: 200 } });
      setRows(data);
    } catch (e) { toast.error(apiErr(e, "Errore caricamento")); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="compliance-audit-tab">
      <Disclaimer>Log azioni sensibili: login, modifiche cartella, invii referti/promemoria, reply SMS, modifiche compliance. Solo metadati, mai contenuti clinici.</Disclaimer>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-3 items-end">
        <input placeholder="Azione contiene…" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="border border-[#E2E8F0] rounded px-2 py-1.5 text-sm" />
        <input placeholder="Utente (email/id)" value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })} className="border border-[#E2E8F0] rounded px-2 py-1.5 text-sm" />
        <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="border border-[#E2E8F0] rounded px-2 py-1.5 text-sm" />
        <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="border border-[#E2E8F0] rounded px-2 py-1.5 text-sm" />
        <button onClick={load} data-testid="audit-refresh" className="bg-brand-navy hover:bg-brand-navyDark text-white px-3 py-1.5 rounded text-sm font-bold flex items-center justify-center gap-1"><RefreshCw size={14} /> Aggiorna</button>
      </div>
      {rows.length === 0 ? <div className="text-slate-500 italic text-sm py-6 text-center">Nessun evento.</div> : (
        <ul className="space-y-1" data-testid="audit-rows">
          {rows.map((r) => (
            <li key={r.id} className="bg-white border border-[#E2E8F0] rounded-lg p-2 text-xs flex flex-wrap items-center gap-2">
              <span className="font-mono text-slate-500 w-44 truncate">{r.ts ? new Date(r.ts).toLocaleString("it-IT") : (r.at ? new Date(r.at).toLocaleString("it-IT") : "—")}</span>
              <span className="bg-slate-100 px-2 py-0.5 rounded font-bold text-brand-navy">{r.action}</span>
              <span className="text-slate-700">{r.actor_email || r.actor_id || "system"}</span>
              {r.target_type && <span className="text-slate-500">→ {r.target_type}/{r.target_id?.slice?.(0, 8)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RegistroTab() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/compliance/registro"); setRows(data); }
    catch (e) { toast.error(apiErr(e, "Errore caricamento")); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const payload = {};
      ["title", "finalita", "base_giuridica", "categorie_dati", "interessati",
       "soggetti_autorizzati", "destinatari", "trasferimenti_extra_ue",
       "retention", "misure", "note"].forEach((k) => { payload[k] = active[k] || ""; });
      payload.change_summary = active.change_summary || "";
      const { data } = await api.patch(`/admin/compliance/registro/${active.key}`, payload);
      toast.success(`Salvato · versione ${data.version}`);
      setActive(null); load();
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="compliance-registro-tab">
      <Disclaimer>Registro delle attività di trattamento (art. 30 GDPR). Ogni modifica crea una nuova versione tracciata.</Disclaimer>
      <ul className="space-y-2" data-testid="registro-rows">
        {rows.map((r) => (
          <li key={r.key} data-testid={`registro-${r.key}`} className="bg-white border border-[#E2E8F0] rounded-lg p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-brand-navy">{r.title}</div>
                <div className="text-xs text-slate-500 mt-0.5"><b>Finalità:</b> {r.finalita}</div>
                <div className="text-[11px] text-slate-500"><b>Base:</b> {r.base_giuridica}</div>
                <div className="text-[11px] text-slate-500"><b>Retention:</b> {r.retention}</div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">v{r.version}</span>
                <button onClick={() => setActive({ ...r, change_summary: "" })} data-testid={`registro-edit-${r.key}`} className="text-xs text-brand-navy font-bold hover:underline">Modifica</button>
              </div>
            </div>
          </li>
        ))}
      </ul>
      {active && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="registro-edit-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full my-6 shadow-2xl">
            <div className="px-5 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
              <div className="font-extrabold text-brand-navy">{active.title}</div>
              <button onClick={() => setActive(null)}><XIcon size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-2 text-sm max-h-[70vh] overflow-y-auto">
              {[
                ["title", "Nome attività"],
                ["finalita", "Finalità"],
                ["base_giuridica", "Base giuridica"],
                ["categorie_dati", "Categorie di dati"],
                ["interessati", "Categorie interessati"],
                ["soggetti_autorizzati", "Soggetti autorizzati"],
                ["destinatari", "Destinatari"],
                ["trasferimenti_extra_ue", "Trasferimenti extra-UE"],
                ["retention", "Retention"],
                ["misure", "Misure di sicurezza"],
                ["note", "Note interne"],
              ].map(([k, lbl]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{lbl}</label>
                  <textarea rows={k === "note" ? 2 : 1} value={active[k] || ""}
                            onChange={(e) => setActive({ ...active, [k]: e.target.value })}
                            data-testid={`registro-field-${k}`}
                            className="w-full border border-[#E2E8F0] rounded px-2 py-1 mt-0.5 text-xs" />
                </div>
              ))}
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Riepilogo modifica</label>
                <input value={active.change_summary || ""} onChange={(e) => setActive({ ...active, change_summary: e.target.value })}
                       data-testid="registro-field-change-summary"
                       className="w-full border border-[#E2E8F0] rounded px-2 py-1 mt-0.5 text-xs"
                       placeholder="Facoltativo — es. aggiornata clausola trasferimento" />
              </div>
              <button onClick={save} disabled={busy} data-testid="registro-save-btn"
                      className="w-full bg-brand-navy text-white font-bold rounded px-4 py-2 disabled:opacity-60">
                Salva nuova versione
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DataBreachTab() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/compliance/breaches"); setRows(data); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/compliance/breaches", creating);
      toast.success("Incidente segnalato");
      setCreating(null); load();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
    finally { setBusy(false); }
  };
  const patch = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/compliance/breaches/${editing.id}`, {
        status: editing.status, severity: editing.severity,
        measures_taken: editing.measures_taken || "",
        notified_to_authority: !!editing.notified_to_authority,
        notified_to_subjects: !!editing.notified_to_subjects,
        resolution_note: editing.resolution_note || "",
      });
      toast.success("Aggiornato");
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
    finally { setBusy(false); }
  };

  const hoursLeft = (b) => {
    if (!b.notification_deadline_at) return null;
    const diff = new Date(b.notification_deadline_at) - new Date();
    return Math.max(0, Math.floor(diff / 3600000));
  };
  const totals = {
    open: rows.filter((r) => r.status !== "closed").length,
    high: rows.filter((r) => (r.severity === "high" || r.severity === "critical") && r.status !== "closed").length,
    urgent: rows.filter((r) => r.status !== "closed" && hoursLeft(r) !== null && hoursLeft(r) < 24).length,
  };
  const SEVERITY = { low: "bg-slate-100 text-slate-600", medium: "bg-amber-100 text-amber-700",
                     high: "bg-orange-100 text-orange-700", critical: "bg-red-100 text-red-700" };
  const STATUS = { draft: "bg-slate-100 text-slate-500", in_review: "bg-amber-100 text-amber-700",
                   notified: "bg-blue-100 text-blue-700", closed: "bg-emerald-100 text-emerald-700" };

  return (
    <div data-testid="compliance-breaches-tab">
      <Disclaimer>Non inserire dati clinici o nominativi non necessari. Descrivi l&apos;incidente in modo tecnico e minimizzato.</Disclaimer>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-white border border-[#E2E8F0] rounded p-3 text-center"><div className="text-xs text-slate-500">Aperti</div><div className="text-xl font-extrabold text-brand-navy" data-testid="breaches-open-count">{totals.open}</div></div>
        <div className="bg-white border border-orange-200 rounded p-3 text-center"><div className="text-xs text-orange-700">Alto rischio</div><div className="text-xl font-extrabold text-orange-700">{totals.high}</div></div>
        <div className="bg-white border border-red-200 rounded p-3 text-center"><div className="text-xs text-red-700">Deadline &lt;24h</div><div className="text-xl font-extrabold text-red-700">{totals.urgent}</div></div>
      </div>
      <div className="flex justify-end mb-3">
        <button onClick={() => setCreating({ title: "", description: "", severity: "medium", data_involved: "", subjects_count: 0 })}
                data-testid="breach-new-btn"
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-bold flex items-center gap-1"><AlertTriangle size={14} /> Segnala incidente privacy</button>
      </div>

      {rows.length === 0 ? <div className="text-slate-500 italic text-sm py-6 text-center">Nessun incidente registrato.</div> : (
        <ul className="space-y-2" data-testid="breaches-list">
          {rows.map((b) => (
            <li key={b.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3" data-testid={`breach-${b.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-brand-navy">{b.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{b.description}</div>
                  <div className="flex gap-2 mt-1 text-[10px] flex-wrap">
                    <span className={`${SEVERITY[b.severity]} px-2 py-0.5 rounded-full font-bold`}>{b.severity}</span>
                    <span className={`${STATUS[b.status]} px-2 py-0.5 rounded-full font-bold`}>{b.status}</span>
                    {hoursLeft(b) !== null && b.status !== "closed" && (
                      <span className={`px-2 py-0.5 rounded-full font-bold ${hoursLeft(b) < 24 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                        72h: {hoursLeft(b)}h residue
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => setEditing({ ...b })} className="text-xs text-brand-navy font-bold hover:underline" data-testid={`breach-edit-${b.id}`}>Apri</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {(creating || editing) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-lg w-full my-6 shadow-2xl">
            <div className="px-5 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
              <div className="font-extrabold text-brand-navy">{creating ? "Nuovo incidente" : "Aggiorna incidente"}</div>
              <button onClick={() => { setCreating(null); setEditing(null); }}><XIcon size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-2 text-sm max-h-[70vh] overflow-y-auto">
              {creating && (
                <>
                  <input value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} data-testid="breach-field-title" placeholder="Titolo tecnico (no nominativi)" className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
                  <textarea rows={3} value={creating.description} onChange={(e) => setCreating({ ...creating, description: e.target.value })} data-testid="breach-field-desc" placeholder="Descrizione: sistemi coinvolti, azioni immediate. NON inserire dati clinici." className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
                  <select value={creating.severity} onChange={(e) => setCreating({ ...creating, severity: e.target.value })} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 bg-white">
                    <option value="low">Rischio basso</option><option value="medium">Rischio medio</option>
                    <option value="high">Rischio alto</option><option value="critical">Critico</option>
                  </select>
                  <input value={creating.data_involved} onChange={(e) => setCreating({ ...creating, data_involved: e.target.value })} placeholder="Categorie dati coinvolti (tecnico)" className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
                  <input type="number" min="0" value={creating.subjects_count} onChange={(e) => setCreating({ ...creating, subjects_count: parseInt(e.target.value || "0", 10) })} placeholder="Numero stimato interessati" className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
                  <button onClick={create} disabled={busy || !creating.title || !creating.description} data-testid="breach-create-submit" className="w-full bg-red-600 hover:bg-red-700 text-white font-bold rounded px-4 py-2 disabled:opacity-60">Segnala incidente</button>
                </>
              )}
              {editing && (
                <>
                  <div className="text-xs text-slate-600"><b>{editing.title}</b><br />{editing.description}</div>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 bg-white">
                    <option value="draft">Bozza</option><option value="in_review">In valutazione</option>
                    <option value="notified">Notificato</option><option value="closed">Chiuso</option>
                  </select>
                  <select value={editing.severity} onChange={(e) => setEditing({ ...editing, severity: e.target.value })} className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 bg-white">
                    <option value="low">Rischio basso</option><option value="medium">Rischio medio</option>
                    <option value="high">Rischio alto</option><option value="critical">Critico</option>
                  </select>
                  <textarea rows={2} value={editing.measures_taken || ""} onChange={(e) => setEditing({ ...editing, measures_taken: e.target.value })} placeholder="Misure adottate" className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!editing.notified_to_authority} onChange={(e) => setEditing({ ...editing, notified_to_authority: e.target.checked })} /> Notificato al Garante</label>
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!editing.notified_to_subjects} onChange={(e) => setEditing({ ...editing, notified_to_subjects: e.target.checked })} /> Notificato agli interessati</label>
                  <textarea rows={2} value={editing.resolution_note || ""} onChange={(e) => setEditing({ ...editing, resolution_note: e.target.value })} placeholder="Note risoluzione" className="w-full border border-[#E2E8F0] rounded px-2 py-1.5" />
                  <button onClick={patch} disabled={busy} data-testid="breach-patch-submit" className="w-full bg-brand-navy text-white font-bold rounded px-4 py-2 disabled:opacity-60">Salva aggiornamento</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConsentsOverviewTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/compliance/consents-overview").then(({ data }) => setData(data))
      .catch((e) => toast.error(apiErr(e, "Errore")));
  }, []);
  if (!data) return <div className="text-slate-500 py-6 text-center">Caricamento…</div>;
  return (
    <div data-testid="compliance-consents-tab">
      <Disclaimer>Panoramica solo aggregata. Nessun nome, codice fiscale o contatto paziente è mostrato qui.</Disclaimer>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        {[
          {k: "total", label: "Totale", c: "border-slate-200 text-slate-700"},
          {k: "signed", label: "Firmati", c: "border-emerald-200 text-emerald-700"},
          {k: "invited_open", label: "In attesa", c: "border-amber-200 text-amber-700"},
          {k: "revoked", label: "Revocati", c: "border-red-200 text-red-700"},
          {k: "minor", label: "Minorenni", c: "border-blue-200 text-blue-700"},
        ].map((s) => (
          <div key={s.k} className={`bg-white border ${s.c} rounded p-3 text-center`} data-testid={`consents-metric-${s.k}`}>
            <div className="text-xs uppercase font-bold tracking-wider">{s.label}</div>
            <div className="text-2xl font-extrabold">{data[s.k] ?? 0}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Distribuzione per stato / canale</div>
        {(data.by_status_channel || []).length === 0 ? <div className="text-xs text-slate-400 italic">Nessun dato.</div> : (
          <table className="w-full text-xs" data-testid="consents-breakdown-table">
            <thead className="text-slate-500">
              <tr><th className="text-left py-1">Stato</th><th className="text-left">Canale</th><th className="text-left">Revocato</th><th className="text-right">Count</th></tr>
            </thead>
            <tbody>
              {(data.by_status_channel || []).map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1 font-mono">{r.status}</td>
                  <td>{r.channel}</td>
                  <td>{r.revoked ? "sì" : "no"}</td>
                  <td className="text-right font-bold">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function CompliancePanel() {
  const [tab, setTab] = useState("stato");
  return (
    <div data-testid="compliance-panel">
      <div className="flex flex-wrap gap-1 mb-4 border-b border-[#E2E8F0]">
        {SUB_TABS.map((t) => {
          const I = t.I;
          const active = tab === t.k;
          return (
            <button
              key={t.k} data-testid={`compliance-subtab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition ${active ? "border-brand-navy text-brand-navy" : "border-transparent text-slate-500 hover:text-brand-navy"}`}
            >
              <I size={13} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "stato" && <ChecklistTab />}
      {tab === "documenti" && <DocumentsTab />}
      {tab === "fornitori" && <VendorsTab />}
      {tab === "registro" && <RegistroTab />}
      {tab === "breaches" && <DataBreachTab />}
      {tab === "consents" && <ConsentsOverviewTab />}
      {tab === "backup" && <BackupTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}
